import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import '../../Map/Map.css';
import { DESKTOP_PANEL_WIDTH } from '../../Map/components/PanelShell/PanelShell';
import MapControls from '../../Map/components/MapControls/MapControls';
import CadStockLegend from './CadStockLegend';
import {
  getCadStockLegendItems,
  toggleCadStockVisibility,
} from '../utils/cadStockStatus';
import { useMapOrientation } from '../../Map/hooks/useMapOrientation';
import { colors, typography } from '../../styles/theme';
import { useCadAlignFlatView } from '../hooks/useCadAlignFlatView';
import { useCadParcelsLayer } from '../hooks/useCadParcelsLayer';
import { useCadPlanCanvasImage } from '../hooks/useCadPlanCanvasImage';
import { shouldPaintCadMasterplan } from '../utils/cadCanvasImage';
import { applyCadPlanNavigationLimits, useCadPlanMap } from '../hooks/useCadPlanMap';
import MapUnavailable from '../../Map/components/MapUnavailable';
import { setCadPlanBackground } from '../utils/cadPlanMapStyle';
import { getCadPlanBackgroundColor } from '../../Map/styles/mapTheme';
import { useCadProjectPinsLayer } from '../hooks/useCadProjectPinsLayer';
import {
  computeFeatureCollectionBbox,
  projectCadPlanCollection,
  readCadPlanBounds,
} from '../utils/cadGeometry';

const PLAN_FIT_MAX_ZOOM = 22;
/** Un poco más lejos que el tope del mapa, para que la parcela entre con aire. */
const PLAN_LOCATE_MAX_ZOOM = 20;
const PLAN_LOCATE_PAD_EXTRA = 88;
const noop = () => {};

/**
 * Lienzo `display_mode=canvas|image`: los mismos gestos que el mapa (pan, zoom,
 * rotación, pitch, tooltip, click que encuadra), sin teselas detrás.
 * En `image` el PNG entra entero en `bounds` (contain); los lotes van encima.
 * `shareOutline`: vista `/#/lista/:id`, línea blanca y relleno de stock suave.
 * `hoverLineColor`: hover de marca; si no, teal Terrio.
 * `maxZoom`: tope de `/#/lista/:id`; Listas / Referenciar siguen en 22.
 */
export default function ShowroomPlanStage({
  project,
  selectedId,
  onSelect,
  featureCollection,
  panelOpen = false,
  isLoggedIn = false,
  fitPadding: fitPaddingProp,
  topOffset = 0,
  bottomOffset = 0,
  fitRef,
  onPlanClick = null,
  locateRef = null,
  hoverRef = null,
  onHoverParcelId = null,
  highlightedId = null,
  compactShareChrome = false,
  imageTransform = null,
  shareOutline = false,
  hoverLineColor = null,
  maxZoom = null,
  imagePaintUrl = null,
  onImageTransformChange = null,
  adjustImage = false,
  onViewReady = null,
  pickContext = false,
  onPinSelect = null,
  highlightedPinId = null,
}) {
  const [mapInfo, setMapInfo] = useState({ zoom: '' });
  const [planBackground, setPlanBackground] = useState('dark');
  const [initialFitDone, setInitialFitDone] = useState(false);
  const [stockVisibility, setStockVisibility] = useState(null);
  const planBackgroundColor = getCadPlanBackgroundColor(planBackground);
  const onViewReadyRef = useRef(onViewReady);
  onViewReadyRef.current = onViewReady;

  const planCollection = useMemo(
    () => projectCadPlanCollection(
      featureCollection,
      readCadPlanBounds(project, featureCollection),
    ),
    [featureCollection, project],
  );
  const hasGeometry = planCollection.features.some((feature) => feature?.geometry);
  const stockLegendItems = useMemo(
    () => getCadStockLegendItems(featureCollection),
    [featureCollection],
  );
  const handleStockLegendToggle = useCallback((statusId) => {
    setStockVisibility((prev) => toggleCadStockVisibility(prev, statusId));
  }, []);

  const planMaxZoom = Number.isFinite(maxZoom) ? maxZoom : PLAN_FIT_MAX_ZOOM;
  const {
    mapRef,
    mapInstanceRef,
    mapReady,
    mapUnavailable,
    mapUnavailableReason,
  } = useCadPlanMap({
    surface: 'showroom_plan',
    enabled: hasGeometry,
    maxZoom: planMaxZoom,
  });

  const projectKey = project?.id || project?.slug || '';
  useEffect(() => {
    setInitialFitDone(false);
  }, [projectKey]);

  useEffect(() => {
    if (!hasGeometry) setInitialFitDone(true);
  }, [hasGeometry]);

  // Sin WebGL no hay encuadre que esperar: liberar el overlay de carga del padre.
  const viewReady = !hasGeometry || mapUnavailable || (mapReady && initialFitDone);
  useEffect(() => {
    if (viewReady) onViewReadyRef.current?.();
  }, [viewReady]);

  const { mapBearing, mapPitch, handleResetNorth, handleResetPitch } = useMapOrientation(
    mapInstanceRef,
    mapReady,
  );

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return undefined;
    const syncInfo = () => {
      const zoom = map.getZoom().toFixed(1);
      setMapInfo((prev) => (prev.zoom === zoom ? prev : { zoom }));
    };
    syncInfo();
    map.on('move', syncInfo);
    return () => {
      try { map.off('move', syncInfo); } catch (_) { /* noop */ }
    };
  }, [mapInstanceRef, mapReady]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    setCadPlanBackground(map, planBackground);
  }, [mapInstanceRef, mapReady, planBackground]);

  const fitPadding = useMemo(() => {
    if (fitPaddingProp) return fitPaddingProp;
    if (compactShareChrome) {
      return { top: 16, bottom: panelOpen ? 280 : 40, left: 24, right: 24 };
    }
    return {
      top: 16,
      bottom: 40,
      left: panelOpen ? DESKTOP_PANEL_WIDTH + 40 : 40,
      right: 40,
    };
  }, [compactShareChrome, fitPaddingProp, panelOpen]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !hasGeometry) return;
    applyCadPlanNavigationLimits(map, computeFeatureCollectionBbox(planCollection));
  }, [hasGeometry, mapInstanceRef, mapReady, planCollection]);

  useCadPlanCanvasImage({
    mapInstanceRef,
    enabled: hasGeometry && mapReady,
    project,
    featureCollection,
    transform: imageTransform,
    paintUrl: imagePaintUrl,
    onTransformChange: onImageTransformChange,
    adjustEnabled: adjustImage,
  });

  const { locateParcel, hoverParcel, fitAllParcels } = useCadParcelsLayer({
    mapInstanceRef,
    enabled: hasGeometry && mapReady,
    featureCollection: planCollection,
    selectedId: onPlanClick || adjustImage ? null : selectedId,
    highlightedId: onPlanClick || adjustImage ? null : highlightedId,
    onSelect: onPlanClick || adjustImage ? undefined : onSelect,
    fitPadding,
    fitOnLoad: true,
    fitMaxZoom: planMaxZoom,
    locateMaxZoom: Math.min(PLAN_LOCATE_MAX_ZOOM, planMaxZoom),
    locatePaddingExtra: Number.isFinite(maxZoom) ? 40 : PLAN_LOCATE_PAD_EXTRA,
    stockVisibility,
    alignMode: adjustImage,
    masterplanEnabled: shouldPaintCadMasterplan(project, { alignMode: adjustImage }),
    shareOutline,
    hoverLineColor,
    initialFitDuration: 0,
    onInitialFit: () => setInitialFitDone(true),
    pickContext,
    pickEnabled: !onPlanClick && !adjustImage,
    onHoverParcelId,
  });

  useCadProjectPinsLayer({
    mapInstanceRef,
    project,
    featureCollection,
    paintMode: 'plan',
    enabled: hasGeometry && mapReady && !adjustImage,
    interactive: !onPlanClick,
    onSelectPin: onPinSelect,
    highlightedPinId,
    tooltipVariant: shareOutline ? 'share' : 'place',
  });

  useCadAlignFlatView({
    mapInstanceRef,
    enabled: adjustImage && mapReady,
  });

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !onPlanClick) return undefined;
    const canvas = map.getCanvas();
    const container = typeof map.getContainer === 'function' ? map.getContainer() : null;
    const handler = (event) => {
      const lng = Number(event?.lngLat?.lng);
      const lat = Number(event?.lngLat?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      onPlanClick({ lng, lat });
    };
    const setCrosshair = () => {
      try { canvas.style.cursor = 'crosshair'; } catch (_) { /* noop */ }
    };
    setCrosshair();
    try { container?.classList.add('terrio-map-crosshair'); } catch (_) { /* noop */ }
    map.on('click', handler);
    map.on('mousemove', setCrosshair);
    map.on('mouseenter', setCrosshair);
    return () => {
      map.off('click', handler);
      map.off('mousemove', setCrosshair);
      map.off('mouseenter', setCrosshair);
      try { container?.classList.remove('terrio-map-crosshair'); } catch (_) { /* noop */ }
      try { canvas.style.cursor = ''; } catch (_) { /* noop */ }
    };
  }, [mapInstanceRef, mapReady, onPlanClick]);

  useEffect(() => {
    if (!fitRef) return undefined;
    fitRef.current = () => fitAllParcels();
    return () => {
      fitRef.current = null;
    };
  }, [fitAllParcels, fitRef]);

  useEffect(() => {
    if (!locateRef) return undefined;
    locateRef.current = locateParcel;
    return () => {
      locateRef.current = null;
    };
  }, [locateParcel, locateRef]);

  useEffect(() => {
    if (!hoverRef) return undefined;
    hoverRef.current = hoverParcel;
    return () => {
      hoverRef.current = null;
    };
  }, [hoverParcel, hoverRef]);

  useEffect(() => {
    if (onPlanClick || !selectedId) return;
    locateParcel(selectedId);
  }, [locateParcel, onPlanClick, selectedId]);

  if (!hasGeometry) {
    return (
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        bgcolor: colors.backgroundDark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
      >
        <Typography sx={{
          color: colors.textSecondary,
          fontSize: '13px',
          fontFamily: typography.fontFamily.primary,
          textAlign: 'center',
        }}
        >
          Esta lista no tiene geometría para el plano.
        </Typography>
      </Box>
    );
  }

  if (mapUnavailable) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: colors.backgroundDark }}>
        <MapUnavailable variant="stage" reason={mapUnavailableReason} />
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      bgcolor: planBackgroundColor,
    }}
    >
      <MapControls
        mapView="satellite1"
        onSelectMapView={noop}
        isLoggedIn={isLoggedIn}
        hideParcelasToggle
        hideReferenceLayersToggle
        hideMapViewToggle
        hideFeedbackButton
        planBackground={planBackground}
        onPlanBackgroundChange={setPlanBackground}
        centerButtonMode="fit"
        onCenterOnUserLocation={() => fitAllParcels()}
        onZoomIn={() => mapInstanceRef.current?.zoomIn()}
        onZoomOut={() => mapInstanceRef.current?.zoomOut()}
        mapInfo={mapInfo}
        disabledMessage=""
        mapBearing={mapBearing}
        mapPitch={mapPitch}
        onResetNorth={handleResetNorth}
        onResetPitch={handleResetPitch}
        topOffset={topOffset}
        bottomOffset={bottomOffset}
        compactShareChrome={compactShareChrome}
        overlayLegend={!adjustImage && stockLegendItems.length
          ? (
            <CadStockLegend
              items={stockLegendItems}
              visibility={stockVisibility}
              onToggle={handleStockLegendToggle}
            />
          )
          : null}
      />

      <div
        ref={mapRef}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />

      {(!mapReady || !initialFitDone) && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1200,
          bgcolor: planBackgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        >
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      )}
    </Box>
  );
}
